/* eslint-disable */
import * as React from "react";
import {
  createContext,
  useState,
  useLayoutEffect,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";

/**
 * These hooks and utilities are largely copied from the @reach-ui/descendents package. They consider the package
 * pre-release and unstable, so instead of importing it, we copied it here and changed the types from TypeScript to
 * Flow. If the package ever stabilizes, we can import these functions from there and remove this file.
 */
export type TypeDescendant<ElementType, DescendantProps> = DescendantProps & {
  element: ElementType | null;
  index: number;
};
type TypeDescendantContext<ElementType, DescendantProps> = {
  descendants: TypeDescendant<ElementType, DescendantProps>[];
  registerDescendant(
    descendant: Omit<TypeDescendant<ElementType, DescendantProps>, "index">
  ): void;
  unregisterDescendant(element: ElementType | null): void;
};

const noop = () => {};

/**
 * Returns the previous value of a reference after a component update.
 *
 * @param value
 */
export function usePrevious(value) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

/**
 * Wraps a lib-defined event handler and a user-defined event handler, returning
 * a single handler that allows a user to prevent lib-defined handlers from
 * firing.
 *
 * @param theirHandler User-supplied event handler
 * @param ourHandler Library-supplied event handler
 */
export function wrapEvent<EventType extends React.SyntheticEvent | Event>(
  theirHandler: ((event: EventType) => any) | null | undefined,
  ourHandler: (event: EventType) => any
): (event: EventType) => any {
  return (event) => {
    theirHandler && theirHandler(event);

    if (!event.defaultPrevented) {
      return ourHandler(event);
    }
  };
}

export function canUseDOM() {
  return (
    typeof window !== "undefined" &&
    typeof window.document !== "undefined" &&
    typeof window.document.createElement !== "undefined"
  );
}
const useIsomorphicLayoutEffect = canUseDOM() ? useLayoutEffect : useEffect;

export function createNamedContext<ContextValueType>(
  name: string,
  defaultValue: ContextValueType
): React.Context<ContextValueType> {
  const Ctx = createContext<ContextValueType>(defaultValue);
  Ctx.displayName = name;
  return Ctx;
}

export function createDescendantContext(
  name: string,
  initialValue = {
    descendants: [],
    registerDescendant: noop,
    unregisterDescendant: noop,
  }
) {
  return createNamedContext(name, {
    ...initialValue,
  });
}

/**
 * This hook registers our descendant by passing it into an array. We can then
 * search that array by to find its index when registering it in the component.
 * We use this for focus management, keyboard navigation, and typeahead
 * functionality for some components.
 *
 * The hook accepts the element node and (optionally) a key. The key is useful
 * if multiple descendants have identical text values and we need to
 * differentiate siblings for some reason.
 *
 * Our main goals with this are:
 *   1) maximum composability,
 *   2) minimal API friction
 *   3) SSR compatibility*
 *   4) concurrent safe
 *   5) index always up-to-date with the tree despite changes
 *   6) works with memoization of any component in the tree (hopefully)
 *
 * * As for SSR, the good news is that we don't actually need the index on the
 * server for most use-cases, as we are only using it to determine the order of
 * composed descendants for keyboard navigation. However, in the few cases where
 * this is not the case, we can require an explicit index from the app.
 */
export function useDescendant<ElementType, DescendantProps>(
  { context, element, ...rest },
  indexProp?: number
) {
  let [, forceUpdate] = useState<any>();
  let { registerDescendant, unregisterDescendant, descendants } =
    useContext<TypeDescendantContext<ElementType, DescendantProps>>(context);
  // Prevent any flashing
  useIsomorphicLayoutEffect(() => {
    if (!element) forceUpdate({});
    registerDescendant({
      element,
      ...rest,
    } as any);
    return () => unregisterDescendant(element);
  }, [element, ...Object.values(rest)]);
  return indexProp
    ? indexProp
    : descendants.findIndex((item) => item.element === element);
}
export function useDescendants<ElementType, DescendantProps>() {
  return useState<TypeDescendant<ElementType, DescendantProps>[]>([]);
}
export function DescendantProvider<
  ElementType extends HTMLElement,
  DescendantProps
>({
  context: Ctx,
  children,
  items,
  set,
}: {
  context: any;
  children: React.ReactNode;
  items: TypeDescendant<ElementType, DescendantProps>[];
  set: (
    arg0: (arg0: TypeDescendant<ElementType, DescendantProps>[]) => any
  ) => any;
}) {
  let registerDescendant = useCallback(
    ({ element, ...rest }) => {
      if (!element) {
        return;
      }

      set((items) => {
        let newItem;
        let newItems;

        // If there are no items, register at index 0 and bail.
        if (items.length === 0) {
          newItem = {
            element,
            index: 0,
            ...rest,
          };
          newItems = [...items, newItem];
        } else if (items.find((item) => item.element === element)) {
          // If the element is already registered, just use the same array
          newItems = items;
        } else {
          // When registering a descendant, we need to make sure we insert in
          // into the array in the same order that it appears in the DOM. So as
          // new descendants are added or maybe some are removed, we always know
          // that the array is up-to-date and correct.
          //
          // So here we look at our registered descendants and see if the new
          // element we are adding appears earlier than an existing descendant's
          // DOM node via `node.compareDocumentPosition`. If it does, we insert
          // the new element at this index. Because `registerDescendant` will be
          // called in an effect every time the descendants state value changes,
          // we should be sure that this index is accurate when descendent
          // elements come or go from our component.
          let index = items.findIndex((item) => {
            if (!item.element || !element) {
              return false;
            }

            // Does this element's DOM node appear before another item in the
            // array in our DOM tree? If so, return true to grab the index at
            // this point in the array so we know where to insert the new
            // element.
            return Boolean(
              item.element.compareDocumentPosition(element) &
                Node.DOCUMENT_POSITION_PRECEDING
            );
          });
          newItem = {
            element,
            index,
            ...rest,
          };

          // If an index is not found we will push the element to the end.
          if (index === -1) {
            newItems = [...items, newItem];
          } else {
            newItems = [
              ...items.slice(0, index),
              newItem,
              ...items.slice(index),
            ];
          }
        }

        return newItems.map((item, index) => ({ ...item, index }));
      });
    }, // set is a state setter initialized by the useDescendants hook.
    // We can safely ignore the lint warning here because it will not change
    // between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  let unregisterDescendant = useCallback(
    (element: TypeDescendant<ElementType, DescendantProps>["element"]) => {
      if (!element) {
        return;
      }

      set((items) =>
        items
          .filter((item) => element !== item.element)
          .map((item, index) => ({ ...item, index }))
      );
    }, // set is a state setter initialized by the useDescendants hook.
    // We can safely ignore the lint warning here because it will not change
    // between renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const value = useMemo(() => {
    return {
      descendants: items,
      registerDescendant,
      unregisterDescendant,
    };
  }, [items, registerDescendant, unregisterDescendant]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
export function useDescendantKeyDown<
  ElementType,
  DescendantProps,
  K extends keyof TypeDescendant<ElementType, DescendantProps>
>(
  context: React.Context<TypeDescendantContext<ElementType, DescendantProps>>,
  options: {
    currentIndex?: number;
    key?: K | "option";
    filter?: (
      descendant: TypeDescendant<ElementType, DescendantProps>
    ) => boolean;
    orientation?: "vertical" | "horizontal" | "both";
    rotate?: boolean;
    rtl?: boolean;
    callback(
      nextOption:
        | TypeDescendant<ElementType, DescendantProps>
        | TypeDescendant<ElementType, DescendantProps>[keyof TypeDescendant<
            ElementType,
            DescendantProps
          >]
    ): void;
  }
) {
  let { descendants } = useContext(context);
  let {
    callback,
    currentIndex,
    filter,
    key = "index",
    orientation = "vertical",
    rotate = true,
    rtl = false,
  } = options;
  let index = currentIndex ? currentIndex : -1;
  return function handleKeyDown(event: React.KeyboardEvent) {
    if (
      ![
        "ArrowDown",
        "ArrowUp",
        "ArrowLeft",
        "ArrowRight",
        "PageUp",
        "PageDown",
        "Home",
        "End",
      ].includes(event.key)
    ) {
      return;
    }

    // If we use a filter function, we need to re-index our descendants array
    // so that filtered descendent elements aren't selected.
    let selectableDescendants = filter
      ? descendants.filter(filter)
      : descendants;

    // Current index should map to the updated array vs. the original
    // descendants array.
    if (filter) {
      index = selectableDescendants.findIndex(
        (descendant) => descendant.index === currentIndex
      );
    }

    // We need some options for any of this to work!
    if (!selectableDescendants.length) {
      return;
    }

    function getNextOption() {
      let atBottom = index === selectableDescendants.length - 1;
      return atBottom
        ? rotate
          ? getFirstOption()
          : selectableDescendants[index]
        : selectableDescendants[(index + 1) % selectableDescendants.length];
    }

    function getPreviousOption() {
      let atTop = index === 0;
      return atTop
        ? rotate
          ? getLastOption()
          : selectableDescendants[index]
        : selectableDescendants[
            (index - 1 + selectableDescendants.length) %
              selectableDescendants.length
          ];
    }

    function getFirstOption() {
      return selectableDescendants[0];
    }

    function getLastOption() {
      return selectableDescendants[selectableDescendants.length - 1];
    }

    switch (event.key) {
      case "ArrowDown":
        if (orientation === "vertical" || orientation === "both") {
          event.preventDefault();
          let next = getNextOption() as any; // @ts-notes: sincerely dont understand the intent of the original library here, since the supposed reference has been completely refactored
          callback(key === "option" ? next : next[key]);
        }

        break;

      case "ArrowUp":
        if (orientation === "vertical" || orientation === "both") {
          event.preventDefault();
          let prev = getPreviousOption() as any;
          callback(key === "option" ? prev : prev[key]);
        }

        break;

      case "ArrowLeft":
        if (orientation === "horizontal" || orientation === "both") {
          event.preventDefault();
          let nextOrPrev = (rtl ? getNextOption : getPreviousOption)() as any;
          callback(key === "option" ? nextOrPrev : nextOrPrev[key]);
        }

        break;

      case "ArrowRight":
        if (orientation === "horizontal" || orientation === "both") {
          event.preventDefault();
          let prevOrNext = (rtl ? getPreviousOption : getNextOption)() as any;
          callback(key === "option" ? prevOrNext : prevOrNext[key]);
        }

        break;

      case "PageUp":
        event.preventDefault();
        let prevOrFirst = (
          event.ctrlKey ? getPreviousOption : getFirstOption
        )() as any;
        callback(key === "option" ? prevOrFirst : prevOrFirst[key]);
        break;

      case "Home":
        event.preventDefault();
        let first = getFirstOption() as any;
        callback(key === "option" ? first : first[key]);
        break;

      case "PageDown":
        event.preventDefault();
        let nextOrLast = (
          event.ctrlKey ? getNextOption : getLastOption
        )() as any;
        callback(key === "option" ? nextOrLast : nextOrLast[key]);
        break;

      case "End":
        event.preventDefault();
        let last = getLastOption() as any;
        callback(key === "option" ? last : last[key]);
        break;
    }
  };
}
